import {
  ApiAllUserApplicationsListQueryParams,
  useApiAccountsUsersList,
  useApiAllUserApplicationsList,
  useApiPvpApplicationsList,
  useApiPvpList,
} from "@/api/lctApIComponents";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { StatusEnum } from "@/api/lctApISchemas";
import { useNavigate } from "@tanstack/react-router";
import { StatusPill } from "@/pvp/components/StatusPill";
import { statusOptions, TABLE_IDS } from "@/utils/constants";
import { FilterConfig } from "@/components/DataTable/Filters/TableFilters";
import { useGetCurrentUser } from "@/hooks/useGetCurrentUser";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useDebounce } from "use-debounce";
interface IProps {}

export const ApplicationTable: React.FC<IProps> = () => {
  const token = useAppSelector((state) => state.user.authKey);
  const [selectedPvp, setSelectedPvp] = useState<string | undefined>();
  const [selectedApp, setSelectedApp] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [selectedUser, setSelectedUser] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPvp, selectedApp, selectedStatus, selectedUser, debouncedSearchQuery]);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };
  const { currentUser } = useGetCurrentUser();
  const { data: pvpData, isLoading: pvpDataLoading } = useApiPvpList({ headers: { Authorization: `Token ${token}` } }, { staleTime: 1000 });
  const pvpOptions = pvpData?.results.map((pvp) => ({ label: pvp.title, value: pvp.id }));

  const { data: appData, isLoading: appDataLoading } = useApiPvpApplicationsList(
    { headers: { Authorization: `Token ${token}` }, pathParams: { pvp: selectedPvp ?? "" } },
    { enabled: !!selectedPvp && !!pvpOptions },
  );
  const appOptions = appData?.results.map((app) => ({ label: app.title, value: app.id }));

  const { data: userData, isLoading: userDataLoading } = useApiAccountsUsersList({ headers: { Authorization: `Token ${token}` } });

  const userOptions = userData?.results.map((user) => ({
    label: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username,
    value: `${user.pk}`,
  }));

  const queryParams = useMemo((): ApiAllUserApplicationsListQueryParams => {
    const params: Record<string, string | number | undefined> = {};
    if (selectedPvp) {
      params["application__pvp"] = selectedPvp;
    }
    if (selectedStatus) {
      params["status"] = selectedStatus as StatusEnum;
    }
    if (selectedApp) {
      params["application"] = selectedApp;
    }
    if (selectedUser) {
      params["user"] = +selectedUser;
    }
    if (debouncedSearchQuery) {
      params["search"] = debouncedSearchQuery;
    }
    params["page"] = currentPage;
    params["page_size"] = pageSize;
    return params as ApiAllUserApplicationsListQueryParams;
  }, [selectedPvp, selectedApp, selectedStatus, selectedUser, debouncedSearchQuery, currentPage, pageSize]);

  const { data, isLoading } = useApiAllUserApplicationsList({
    headers: { Authorization: `Token ${token}` },
    queryParams,
  });

  type Data = {
    name: string;
    applicationName: string;
    id: string;
    status: StatusEnum | undefined;
    email: string;
    createdAt: string;
    updatedAt: string;
    pvp: string;
    appId: string;
    pvpId: string;
    application: string;
    userAppId: string;
    carbonScore: string | undefined;
  };

  const tableData: Data[] = useMemo(() => {
    return (
      data?.results.map((app) => ({
        name: `${app.user.first_name} ${app.user.last_name}`,
        applicationName: app.name || "-",
        email: app.user.email,
        id: app.id,
        appId: app.application.id,
        pvpId: app.application.pvp.id,
        application: app.application.title,
        status: app.status,
        createdAt: app.created_at,
        updatedAt: app.updated_at,
        pvp: app.application.pvp.title,
        userAppId: app.user_app_id,
        carbonScore: app.carbon_score,
      })) ?? []
    );
  }, [data?.results]);

  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const navigateToApplication = (row: Data) => {
    navigate({
      to: "/admin/pvp/$pvpId",
      params: { pvpId: row.pvpId },
      search: { userAppId: row.id, appId: row.appId, email: row.email },
    });
  };

  const { accessor } = createColumnHelper<Data>();

  const columns = useMemo(() => {
    return [
      accessor("userAppId", {
        id: "userAppId",
        header: "Application ID",
        minSize: 250,
        cell: (info) => info.getValue(),
        meta: {
          canCopy: true,
        },
      }),
      accessor("applicationName", {
        id: "applicationName",
        header: "Application Name",
        cell: (info) => info.getValue(),
        meta: {
          canCopy: true,
        },
      }),
      accessor("name", {
        id: "name",
        header: "Name",
        cell: (info) => info.getValue(),
      }),
      accessor("email", {
        id: "email",
        header: "Email",
        cell: (info) => info.getValue(),
        meta: {
          isEmail: true,
          canCopy: true,
          emailTemplate: {
            getSubject: (row: Data) => `LCT Application Update - ${row.userAppId}`,
            getBody: (row: Data) =>
              `Dear ${row.name},\n\n` +
              `I am writing regarding your LCT application (Reference No.: ${row.userAppId}).\n` +
              `Current status: ${statusOptions.find((option) => option.value === row.status)?.label}\n\n` +
              `Best regards,\n${currentUser.fullName}`,
          },
        },
      }),
      accessor("status", {
        id: "status",
        header: "Application status",
        minSize: 250,
        cell: ({ getValue }) => <StatusPill applicationStatus={getValue()} />,
      }),
      accessor("createdAt", {
        id: "createdAt",
        header: "Started",
        cell: (info) => DateTime.fromISO(info.getValue()).toFormat("MM/dd/yyyy"),
      }),
      accessor("updatedAt", {
        id: "updatedAt",
        header: "Last updated",
        cell: (info) => DateTime.fromISO(info.getValue()).toFormat("MM/dd/yyyy"),
      }),
      accessor("pvp", {
        id: "pvp",
        header: "Certification Program",
        cell: (info) => info.getValue(),
      }),
      accessor("application", {
        id: "application",
        header: "Certification Application",
        cell: (info) => info.getValue(),
      }),
      accessor("carbonScore", {
        id: "carbonScore",
        header: "Carbon Score",
        cell: (info) => info.getValue() ?? "-",
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterConfig: FilterConfig[] = [
    {
      label: "Certification Program",
      options: pvpOptions ?? [],
      selected: selectedPvp,
      onChange: setSelectedPvp,
      optionsLoading: pvpDataLoading,
    },
    selectedPvp && appOptions && appOptions.length > 1
      ? {
          label: "Certification Application",
          options: appOptions ?? [],
          selected: selectedApp,
          onChange: setSelectedApp,
          optionsLoading: appDataLoading,
        }
      : undefined,
    {
      label: "Status",
      options: statusOptions,
      selected: selectedStatus,
      onChange: setSelectedStatus,
    },
    {
      label: "User",
      options: userOptions ?? [],
      selected: selectedUser,
      onChange: setSelectedUser,
      optionsLoading: userDataLoading,
    },
  ];

  return (
    <section className="mt-4">
      <DataTable
        tableId={TABLE_IDS.USER_APPLICATIONS_ADMIN}
        columns={columns}
        data={tableData}
        loading={isLoading}
        filterConfig={filterConfig}
        onRowClick={navigateToApplication}
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [10, 25, 50, 100],
        }}
        toolbarActions={
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by application name or user email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        }
      />
    </section>
  );
};
